import React from "react";
import { Card, CardBody, Container, Row, Col, Button } from "reactstrap";
import { Link } from "react-router-dom";
import { useTranslation } from "../../hooks/useTranslation";
import { useFetchStorefrontProductsQuery } from "../../api/api";

const HomePage = () => {
  const t = useTranslation();

  const { data: featuredProducts = [], isFetching: isLoadingProducts } =
    useFetchStorefrontProductsQuery();

  // Get first 6 products for featured section
  const displayProducts = featuredProducts.slice(0, 6);

  const features = [
    {
      title: t("client.fastDelivery") || "Fast Delivery",
      description:
        t("client.fastDeliveryDesc") || "Quick and reliable delivery service",
      icon: "tim-icons icon-delivery-fast",
      color: "primary",
    },
    {
      title: t("client.quality") || "Quality Products",
      description: t("client.qualityDesc") || "Only the best quality products",
      icon: "tim-icons icon-check-2",
      color: "success",
    },
    {
      title: t("client.support") || "24/7 Support",
      description: t("client.supportDesc") || "We're here to help you anytime",
      icon: "tim-icons icon-headphones",
      color: "info",
    },
  ];

  return (
    <div className="client-homepage">
      {/* Hero Section */}
      <section className="hero-section">
        <Container>
          <Row className="align-items-center min-vh-50">
            <Col md="12" className="text-center text-white py-5">
              <h1 className="display-3 font-weight-bold mb-4">
                {t("client.welcome") || "Welcome to Black & White"}
              </h1>
              <p className="lead mb-4" style={{ fontSize: "1.3rem" }}>
                {t("client.welcomeDescription") ||
                  "Discover our premium collection of quality products"}
              </p>
              <Link to="/products">
                <Button color="primary" size="lg" className="mt-3 px-5">
                  {t("client.browseProducts") || "Browse Products"} →
                </Button>
              </Link>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Features Section */}
      <section className="features-section py-5">
        <Container>
          <Row>
            <Col md="12" className="text-center mb-5">
              <h2 className="display-4 mb-3">
                {t("client.whyChooseUs") || "Why Choose Us?"}
              </h2>
            </Col>
          </Row>
          <Row>
            {features.map((feature, index) => (
              <Col lg="4" md="6" sm="12" key={index} className="mb-4">
                <Card className="feature-card h-100 border-0 shadow-sm">
                  <CardBody className="text-center p-4">
                    <div
                      className={`icon icon-shape bg-gradient-${feature.color} text-white rounded-circle shadow mb-4`}
                      style={{
                        width: "80px",
                        height: "80px",
                        margin: "0 auto",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <i
                        className={feature.icon}
                        style={{ fontSize: "2rem" }}
                      ></i>
                    </div>
                    <h4 className="card-title mb-3">{feature.title}</h4>
                    <p className="text-muted">{feature.description}</p>
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Featured Products Section */}
      {displayProducts.length > 0 && (
        <section className="products-section py-5 bg-light">
          <Container>
            <Row>
              <Col md="12" className="text-center mb-5">
                <h2 className="display-4 mb-3">
                  {t("client.featuredProducts") || "Featured Products"}
                </h2>
                <p className="lead text-muted">
                  {t("client.featuredProductsDesc") ||
                    "Check out some of our best products"}
                </p>
              </Col>
            </Row>
            <Row>
              {isLoadingProducts ? (
                <Col md="12" className="text-center">
                  <p>{t("common.loading") || "Loading..."}</p>
                </Col>
              ) : displayProducts.length > 0 ? (
                displayProducts.map((product, index) => (
                  <Col
                    lg="4"
                    md="6"
                    sm="12"
                    key={product.id || index}
                    className="mb-4"
                  >
                    <Card className="product-card h-100 border-0 shadow-sm">
                      <CardBody className="p-4">
                        <div className="product-info">
                          <h5 className="product-brand mb-2">
                            {product.brand || "—"}
                          </h5>
                          <p className="product-details text-muted mb-2">
                            <small>
                              {product.color && `${product.color} • `}
                              {product.number && `Size: ${product.number} • `}
                              {product.specific_type && product.specific_type}
                            </small>
                          </p>
                          <div className="product-code mb-3">
                            <span className="badge badge-primary">
                              Code: {product.code || "—"}
                            </span>
                          </div>
                          {product.price && (
                            <div className="product-price">
                              <h4 className="text-primary mb-0">
                                €{Number(product.price).toFixed(2)}
                              </h4>
                            </div>
                          )}
                        </div>
                      </CardBody>
                    </Card>
                  </Col>
                ))
              ) : (
                <Col md="12" className="text-center">
                  <p className="text-muted">
                    {t("client.noProducts") || "No products available"}
                  </p>
                </Col>
              )}
            </Row>
            {displayProducts.length > 0 && (
              <Row className="mt-4">
                <Col md="12" className="text-center">
                  <Link to="/products">
                    <Button color="primary" size="lg">
                      {t("client.viewAllProducts") || "View All Products"} →
                    </Button>
                  </Link>
                </Col>
              </Row>
            )}
          </Container>
        </section>
      )}

      {/* CTA Section */}
      <section className="cta-section py-5">
        <Container>
          <Row>
            <Col md="12">
              <Card className="bg-gradient-primary text-white border-0 shadow-lg">
                <CardBody className="text-center p-5">
                  <h2 className="display-4 mb-3">
                    {t("client.readyToShop") || "Ready to Shop?"}
                  </h2>
                  <p className="lead mb-4">
                    {t("client.readyToShopDesc") ||
                      "Explore our full collection of premium products"}
                  </p>
                  <Link to="/products">
                    <Button color="white" size="lg" className="px-5">
                      {t("client.shopNow") || "Shop Now"}
                    </Button>
                  </Link>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default HomePage;
